/**
 * 字体栈集中定义 —— 全项目唯一来源
 *
 * 背景：默认中文字体从 Kiwi Maru（日文字体，缺约 22% 简体字）换成小赖体 Xiaolai SC（简体全覆盖），
 * 修复同一句话里两种字体交替显示（「打架」）的问题。
 *
 * 优先级三态由 apps/stage-web/src/composables/custom-font.ts 的 buildFontStack() 组合：
 *   1. 用户上传自定义字体  → custom-user-font 最优先
 *   2. 「字体跟随系统」开启 → 系统字体优先、小赖兜底（FOLLOW_SYSTEM_FONT_STACK）
 *   3. 默认（开关关闭）     → 小赖优先（DEFAULT_FONT_STACK）
 *
 * ⚠️ 同步契约：DEFAULT_FONT_STACK 必须与 uno.config.ts 的 theme.fontFamily.sans / cute 保持一致。
 *    uno.config.ts 是构建期 Node 配置，无法直接 import 本常量，故为有意的重复；改动字体栈时务必两边同步。
 */

/** 用户上传字体在 FontFace 中注册的 family 名 */
export const CUSTOM_FONT_FAMILY = 'custom-user-font'

/** 默认（中文优先）：小赖体在前，系统字体兜底 */
export const DEFAULT_FONT_STACK = `"Xiaolai SC", system-ui, ui-sans-serif, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "WenQuanYi Micro Hei", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`

/**
 * 跟随系统：完全交给操作系统决定字体，只用通用关键字、不写任何具体字体名。
 *
 * ⚠️ 这条栈里绝对不要出现具体字体名（包括内嵌的 "Xiaolai SC"，也包括 PingFang / 雅黑 / Noto 等）。
 *    原因有二：
 *      1. 字体名枚举不完——安卓厂商字体（MIUI 兰亭、OneUI、鸿蒙黑体）、用户自装的第三方字体、
 *         各种定制 ROM 千奇百怪，列多少都有漏；漏掉的设备逐字回退又会跑偏。
 *      2. 内嵌字体一定存在，一旦排在通用关键字之前，会拦截系统回退、让「跟随系统」对中文形同虚设
 *         （此前 Android 上明明开了开关却仍显示小赖，就是这个原因）。
 *    通用关键字才是「跟随系统」的正解：
 *      - system-ui → 系统 UI 字体（iOS SF / Windows Segoe，缺中文时由系统自身接管）
 *      - sans-serif → 系统默认无衬线字体。安卓定制 ROM 换字体、用户在系统设置里选字体，
 *        改的就是 sans-serif 的指向，所以无论用户字体叫什么都会被正确解析到。
 *    末尾 emoji 字体只匹配 emoji 字形且排在 sans-serif 之后，不影响中文回退。
 */
export const FOLLOW_SYSTEM_FONT_STACK = `system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`

/**
 * 组合最终字体栈。
 * @param followSystem 是否「字体跟随系统」
 * @param hasCustomFont 是否已成功加载用户自定义字体
 */
export function buildFontStack(followSystem: boolean, hasCustomFont: boolean): string {
  const base = followSystem ? FOLLOW_SYSTEM_FONT_STACK : DEFAULT_FONT_STACK
  return hasCustomFont ? `"${CUSTOM_FONT_FAMILY}", ${base}` : base
}
